<template>
  <div role="main" class="container pb-3 pt-0">
    <!-- Título + botón calendario -->
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h2 class="font-weight-bold mb-0 titulo-cap">Cursos del día</h2>
      <div class="d-flex">
        <button
          :class="[
            'btn btn-icon mr-2',
            esHoy ? 'btn-primary' : 'btn-outline-primary',
          ]"
          @click="irHoy"
          aria-label="Calendario"
        >
          Hoy
        </button>
        <button
          class="btn btn-outline-primary btn-icon"
          @click="goCalendar"
          aria-label="Calendario"
        >
          <i class="fas fa-calendar-alt fa-lg"></i>
        </button>
      </div>
    </div>

    <!-- Navegación semanal -->
    <section class="week-navigator mb-3" aria-label="Navegación semanal">
      <div class="week-header">
        <button
          class="week-arrow"
          @click="shiftWeek(-1)"
          aria-label="Semana anterior"
        >
          <i class="fas fa-chevron-left"></i>
        </button>

        <div class="week-title">
          <strong>{{ weekRangeLabel }}</strong>
          <span>{{ currentDayLabel }}</span>
        </div>

        <button
          class="week-arrow"
          @click="shiftWeek(1)"
          aria-label="Semana siguiente"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>

      <div class="week-strip">
        <button
          v-for="day in weekDays"
          :key="day.key"
          :class="[
            'week-day',
            day.isSelected ? 'week-day--selected' : '',
            day.isToday ? 'week-day--today' : '',
          ]"
          @click="selectDate(day.date)"
          :aria-pressed="day.isSelected ? 'true' : 'false'"
        >
          <span class="week-day__name">{{ day.weekday }}</span>
          <span class="week-day__number">{{ day.dayNumber }}</span>
          <span class="week-day__dots">
            <span
              v-for="status in day.statuses.slice(0, 5)"
              :key="status.id"
              class="week-day__dot"
              :class="'week-day__dot--' + status.key"
              :title="status.label"
              role="img"
              :aria-label="status.label"
            ></span>
            <span
              v-if="day.statuses.length > 5"
              class="week-day__more"
              :title="`${day.statuses.length - 5} curso(s) adicionales`"
              :aria-label="`${day.statuses.length - 5} curso(s) adicionales`"
            >+{{ day.statuses.length - 5 }}</span>
          </span>
        </button>
      </div>
    </section>
    <!-- Lista de cards -->
    <div v-if="loading" class="text-center p-4">
      <div class="spinner-border" role="status"></div>
      <div class="mt-2">Cargando eventos…</div>
    </div>

    <div v-else-if="error" class="alert alert-danger" role="alert">
      {{ error }}
      <button class="btn btn-sm btn-outline-danger" @click="obtenerSemana">
        Reintentar
      </button>
    </div>

    <div v-else-if="events.length === 0" class="empty-courses" role="status">
      <span class="empty-courses__icon" aria-hidden="true">
        <i class="far fa-calendar-alt"></i>
      </span>
      <h3 class="empty-courses__title">Sin cursos para este día</h3>
      <p class="empty-courses__hint">
        Selecciona otro día en el calendario para consultar tus cursos.
      </p>
    </div>

    <div v-else class="events-stack">
      <EventoCard
        v-for="ev in events"
        :key="ev.id"
        :norma="ev.curso.nombre"
        :horaInicio="ev.start"
        :horaFin="ev.end"
        :nombre="ev.cot ? ev.cot.name : ev.title"
        :edo="ev.edo"
        :estadoCapacitador="ev.estado_capacitador"
        @detalle="goDetalle(ev.id)"
      />
    </div>

    <FlashMessage
      :style="{ position: 'fixed', zIndex: 9999, fontSize: '18px' }"
    />
  </div>
</template>

<script>
import axiosInstance from "../../services/axiosInstance";
import EventoCard from "@/components/Capacitador/EventoCard.vue";
export default {
  name: "HomeCapacitador",
  components: { EventoCard },
  data() {
    return {
      refDate: this.parseRouteDate(this.$route?.query?.fecha) || new Date(),
      loading: false,
      error: "",
      weekEvents: {},
      weekRequestId: 0,
    };
  },
  computed: {
    events() {
      return this.weekEvents[this.formatLocalDate(this.refDate)] || [];
    },
    weekKey() {
      return this.formatLocalDate(this.startOfWeek(this.refDate));
    },
    currentDayLabel() {
      const opts = { day: "2-digit", month: "long", year: "numeric" };
      return this.refDate.toLocaleDateString("es-MX", opts);
    },
    weekDays() {
      const start = this.startOfWeek(this.refDate);
      return Array.from({ length: 7 }, (_, index) => {
        const date = new Date(start);
        date.setDate(start.getDate() + index);

        return {
          date,
          key: this.formatLocalDate(date),
          statuses: this.statusesFor(this.formatLocalDate(date)),
          weekday: date
            .toLocaleDateString("es-MX", { weekday: "short" })
            .replace(".", ""),
          dayNumber: String(date.getDate()).padStart(2, "0"),
          isSelected: this.isSameDay(date, this.refDate),
          isToday: this.isSameDay(date, new Date()),
        };
      });
    },
    weekRangeLabel() {
      const start = this.startOfWeek(this.refDate);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);

      const sameMonth = start.getMonth() === end.getMonth();
      const sameYear = start.getFullYear() === end.getFullYear();

      if (sameMonth && sameYear) {
        return `${start.getDate()} - ${end.getDate()} de ${start.toLocaleDateString(
          "es-MX",
          { month: "long" }
        )}`;
      }

      const startLabel = start.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
      });
      const endLabel = end.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
      return `${startLabel} - ${endLabel}`;
    },
    esHoy() {
      const hoy = new Date();
      return (
        this.refDate.getFullYear() === hoy.getFullYear() &&
        this.refDate.getMonth() === hoy.getMonth() &&
        this.refDate.getDate() === hoy.getDate()
      );
    },
  },
  watch: {
    weekKey: {
      immediate: true,
      handler() {
        this.obtenerSemana();
      },
    },
    "$route.query.fecha"(fecha) {
      const parsedDate = this.parseRouteDate(fecha);
      if (!parsedDate || this.isSameDay(parsedDate, this.refDate)) return;

      this.refDate = parsedDate;
    },
  },
  methods: {
    statusesFor(date) {
      const labels = {
        por_confirmar: "Por confirmar",
        confirmado: "Confirmado",
        finalizado: "Finalizado",
        historico: "Histórico",
      };
      return (this.weekEvents[date] || []).map((event, index) => {
        const key = Number(event.edo) === 1
          ? "finalizado"
          : event.estado_capacitador || "por_confirmar";
        return {
          id: event.id ?? index,
          key,
          label: `${event.curso?.nombre || 'Curso'}: ${labels[key] || key}`,
        };
      });
    },
    async obtenerSemana() {
      const requestId = ++this.weekRequestId;
      const week = this.weekKey;
      this.weekEvents = {};
      this.error = "";
      this.loading = true;
      try {
        const { data } = await axiosInstance.get("/cap/index", {
          params: { date: week },
        });
        if (requestId !== this.weekRequestId || week !== this.weekKey) return;
        const days = data?.dias;
        if (
          !days || typeof days !== "object" || Array.isArray(days) ||
          !this.weekDays.every((day) => Array.isArray(days[day.key]))
        ) {
          this.error = "No pudimos cargar los cursos de la semana. Intenta nuevamente en unos momentos.";
          return;
        }
        this.weekEvents = days;
      } catch (error) {
        if (requestId !== this.weekRequestId || week !== this.weekKey) return;
        this.error = "No pudimos cargar los cursos de la semana.";
      } finally {
        if (requestId === this.weekRequestId && week === this.weekKey) {
          this.loading = false;
        }
      }
    },
    irHoy() {
      this.selectDate(new Date(), true);
    },
    formatLocalDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    parseRouteDate(value) {
      if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;

      const [y, m, d] = value.split("-").map((n) => parseInt(n, 10));
      return new Date(y, m - 1, d);
    },
    isSameDay(a, b) {
      return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
      );
    },
    startOfWeek(date) {
      const start = new Date(date);
      const day = start.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      start.setDate(start.getDate() + diff);
      start.setHours(0, 0, 0, 0);
      return start;
    },
    selectDate(date, force = false) {
      const nextDate = new Date(date);
      if (!force && this.isSameDay(nextDate, this.refDate)) return;

      const sameWeek = this.formatLocalDate(this.startOfWeek(nextDate)) === this.weekKey;
      this.refDate = nextDate;
      this.guardarDate();
      if (force && sameWeek) this.obtenerSemana();
    },
    shiftDay(delta) {
      const d = new Date(this.refDate);
      d.setDate(d.getDate() + delta);
      this.selectDate(d);
    },
    shiftWeek(delta) {
      const d = new Date(this.refDate);
      d.setDate(d.getDate() + delta * 7);
      this.selectDate(d);
    },

    goDetalle(ev) {
      this.$router.push({
        path: "/cap-detalles",
        query: { ev: JSON.stringify(ev) },
      });
    },
    goCalendar() {
      const y = this.refDate.getFullYear();
      const m = String(this.refDate.getMonth() + 1).padStart(2, "0");

      this.$router.push({
        path: "/home-cap/calendario",
        query: {
          mes: `${y}-${m}`,
          fecha: this.formatLocalDate(this.refDate),
        },
      });
    },
    guardarDate() {
      const fecha = this.formatLocalDate(this.refDate);
      const nextQuery = { ...this.$route.query, fecha };

      const sameKeys =
        Object.keys(nextQuery).length === Object.keys(this.$route.query).length;
      const sameValues = Object.keys(nextQuery).every(
        (k) => this.$route.query[k] === nextQuery[k]
      );
      if (sameKeys && sameValues) return;

      this.$router.replace({
        path: this.$route.path,
        query: nextQuery,
      });
    },
  },
  mounted() {
    this.guardarDate();
  },
};
</script>

<style scoped>
.empty-courses {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2.25rem 1.25rem;
  text-align: center;
  background: linear-gradient(180deg, #f5f8fd 0%, #ffffff 100%);
  border: 1px solid #e5eaf2;
  border-radius: 12px;
}

.empty-courses__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  margin-bottom: 1rem;
  border-radius: 18px;
  background: #e8effa;
  color: #103d82;
  font-size: 1.6rem;
}

.empty-courses__title {
  margin: 0 0 0.4rem;
  color: #243550;
  font-size: 1.05rem;
  font-weight: 700;
}

.empty-courses__hint {
  max-width: 300px;
  margin: 0.85rem 0 0;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.5;
}

.week-navigator {
  background: #ffffff;
  border: 1px solid #e5eaf2;
  border-radius: 8px;
  padding: 0.55rem;
}

.week-header {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) 32px;
  align-items: center;
  column-gap: 0.5rem;
  margin-bottom: 0.45rem;
}

.week-arrow {
  align-items: center;
  background: transparent;
  border: 0;
  color: #103d82;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  width: 32px;
}

.week-arrow:active {
  transform: scale(0.96);
}

.week-title {
  display: flex;
  flex-direction: column;
  min-width: 0;
  text-align: center;
}

.week-title strong {
  color: #1f2937;
  font-size: 0.95rem;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.week-title span {
  color: #6b7280;
  font-size: 0.75rem;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.week-strip {
  display: grid;
  gap: 0.25rem;
  grid-template-columns: repeat(7, minmax(36px, 1fr));
}

.week-day {
  align-items: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 7px;
  color: #374151;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 52px;
  min-width: 0;
  padding: 0.3rem 0.1rem;
  transition: background-color 0.16s ease, border-color 0.16s ease,
    color 0.16s ease, transform 0.16s ease;
}

.week-day:active {
  transform: translateY(1px);
}

.week-day--selected {
  background: #103d82;
  border-color: #103d82;
  color: #ffffff;
}

.week-day--today:not(.week-day--selected) {
  border-color: #103d82;
  color: #103d82;
}

.week-day__name,
.week-day__number {
  line-height: 1.1;
}

.week-day__name {
  font-size: 0.66rem;
  font-weight: 600;
  text-transform: uppercase;
}

.week-day__number {
  font-size: 1rem;
  font-weight: 800;
  margin-top: 0.15rem;
}

.week-day__dots {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 3px;
  min-height: 8px;
  margin-top: 5px;
}

.week-day__more {
  color: inherit;
  font-size: 0.6rem;
  font-weight: 700;
  line-height: 1;
}

.week-day__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #6b7280;
}

.week-day__dot--por_confirmar { background: #f59e0b; }
.week-day__dot--confirmado { background: #2563eb; }
.week-day__dot--finalizado { background: #16a34a; }
.week-day__dot--historico { background: #6b7280; }

@media (min-width: 768px) {
  .week-navigator {
    padding: 0.75rem;
  }

  .week-strip {
    gap: 0.45rem;
  }

  .week-day {
    min-height: 60px;
  }

  .week-day__number {
    font-size: 1.12rem;
  }
}

@media (max-width: 360px) {
  .week-navigator {
    padding: 0.45rem;
  }

  .week-strip {
    gap: 0.15rem;
    grid-template-columns: repeat(7, minmax(32px, 1fr));
  }

  .week-day {
    min-height: 48px;
  }

  .week-day__name {
    font-size: 0.58rem;
  }

  .week-day__number {
    font-size: 0.92rem;
  }
}
</style>
